import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ExternalLink, RefreshCw } from 'lucide-react';

import PageContainer from '../components/PageContainer';
import { Button, DateInput, EmptyState, LoadingState, SearchSelect, Select, StatusBadge, useToast } from '../components/ui';
import { classPickerOptions, personPickerOptions, type PickableClass, type PickablePerson } from '../utils/pickerOptions';
import { campusApi, checkInsApi, classesApi, usersApi } from '../services/api';
import type { StaffActivity as StaffActivityItem, StaffActivityType, StaffCheckInStatus } from '../types';
import { cn } from '../utils/cn';
import { centerDateKey, formatCenterTime, formatDistance } from '../utils/geolocation';

const typeLabels: Record<StaffActivityType, string> = {
    CHECK_IN: 'Điểm danh cơ sở',
    SESSION_REGISTERED: 'Đăng ký buổi học',
    SESSION_UNREGISTERED: 'Huỷ đăng ký',
    SESSION_REASSIGNED: 'Đổi người',
    SESSION_TIME_CHANGED: 'Sửa giờ',
};

const checkInStatus: Record<StaffCheckInStatus, { label: string; tone: 'success' | 'warning' | 'danger' }> = {
    IN_RANGE: { label: 'Trong phạm vi', tone: 'success' },
    OUT_OF_RANGE: { label: 'Ngoài phạm vi', tone: 'warning' },
    NO_LOCATION: { label: 'Không có vị trí', tone: 'danger' },
};

interface FilterOption {
    id: string;
    name: string;
    campusId?: string | null;
}

function personName(person?: { firstName: string; lastName: string } | null) {
    return person ? `${person.firstName} ${person.lastName}`.trim() : '';
}

function CheckInDetails({ checkIn }: { checkIn: NonNullable<StaffActivityItem['checkIn']> }) {
    const status = checkInStatus[checkIn.status];
    const hasPosition = checkIn.latitude !== null && checkIn.longitude !== null;
    return (
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-500">
            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
            {checkIn.distanceM !== null ? <span>Cách cơ sở ≈{formatDistance(checkIn.distanceM)}</span> : null}
            {checkIn.accuracyM !== null ? <span>Sai số ±{Math.round(checkIn.accuracyM)}m</span> : null}
            {checkIn.locationError ? <span>Lý do: {checkIn.locationError}</span> : null}
            {hasPosition ? (
                <a
                    className="inline-flex items-center gap-1 text-academy-700 hover:underline"
                    href={`https://www.google.com/maps?q=${checkIn.latitude},${checkIn.longitude}`}
                    target="_blank"
                    rel="noreferrer"
                >
                    Xem trên bản đồ <ExternalLink className="h-3 w-3" />
                </a>
            ) : null}
        </div>
    );
}

export default function StaffActivity() {
    const { showToast } = useToast();
    const [searchParams] = useSearchParams();
    const highlightedId = searchParams.get('activityId');
    const [date, setDate] = useState(searchParams.get('date') || centerDateKey());
    const [campusId, setCampusId] = useState('');
    const [classId, setClassId] = useState('');
    const [userId, setUserId] = useState('');
    const [needsReviewOnly, setNeedsReviewOnly] = useState(false);
    const [campuses, setCampuses] = useState<FilterOption[]>([]);
    const [classes, setClasses] = useState<Array<PickableClass & { campusId: string | null }>>([]);
    const [people, setPeople] = useState<PickablePerson[]>([]);
    const [activities, setActivities] = useState<StaffActivityItem[]>([]);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const response = await checkInsApi.getActivities({ date, campusId, classId, userId, needsReview: needsReviewOnly });
            setActivities(response.data.data);
        } catch (error: any) {
            showToast(error?.message || 'Không tải được nhật ký.', 'error');
        } finally {
            setLoading(false);
        }
    }, [date, campusId, classId, userId, needsReviewOnly, showToast]);

    useEffect(() => {
        load();
    }, [load]);

    useEffect(() => {
        campusApi.getAll()
            .then(response => setCampuses(response.data.data || []))
            .catch(() => setCampuses([]));
        classesApi.getAll()
            .then(response => setClasses((response.data.data || [])
                .map((item: any) => ({ ...item, campusId: item.campusId ?? item.campus?.id ?? null }))))
            .catch(() => setClasses([]));
        usersApi.getAll()
            .then(response => setPeople((response.data.data || [])
                .filter((item: any) => item.role === 'TEACHER' || item.role === 'ASSISTANT' || item.role === 'MANAGER' || item.role === 'ADMIN')))
            .catch(() => setPeople([]));
    }, []);

    const classOptions = useMemo(
        () => classPickerOptions(campusId ? classes.filter(item => item.campusId === campusId) : classes),
        [classes, campusId],
    );
    const peopleOptions = useMemo(() => personPickerOptions(people), [people]);

    const changeCampus = (value: string) => {
        setCampusId(value);
        // A class from another campus would make the list silently empty.
        if (value && classId && !classes.some(item => item.id === classId && item.campusId === value)) {
            setClassId('');
        }
    };

    useEffect(() => {
        if (!highlightedId || loading) return;
        document.getElementById(`activity-${highlightedId}`)?.scrollIntoView({ block: 'center' });
    }, [highlightedId, loading]);

    const pendingCount = useMemo(
        () => activities.filter(activity => activity.needsReview && !activity.reviewedAt).length,
        [activities],
    );

    const markReviewed = async (activityId: string) => {
        try {
            await checkInsApi.markReviewed(activityId);
            await load();
        } catch (error: any) {
            showToast(error?.message || 'Không đánh dấu được.', 'error');
        }
    };

    return (
        <PageContainer
            title="Nhật ký nhân sự"
            subtitle="Điểm danh tại cơ sở, đăng ký buổi học và thay đổi giờ của giáo viên/trợ giảng."
            actions={(
                <Button variant="secondary" icon={<RefreshCw className="h-4 w-4" />} onClick={load}>
                    Làm mới
                </Button>
            )}
        >
            <div className="grid gap-2 sm:grid-cols-2 lg:flex lg:flex-wrap lg:items-center">
                <DateInput aria-label="Ngày" className="lg:w-40" value={date} onChange={setDate} clearable={false} />
                <Select aria-label="Lọc theo cơ sở" className="min-h-9 lg:w-44" value={campusId} onChange={event => changeCampus(event.target.value)}>
                    <option value="">Tất cả cơ sở</option>
                    {campuses.map(campus => (
                        <option key={campus.id} value={campus.id}>{campus.name}</option>
                    ))}
                </Select>
                <SearchSelect aria-label="Lọc theo lớp học" className="min-h-9 lg:w-44" value={classId} onChange={setClassId} emptyOption="Tất cả lớp" options={classOptions} />
                <SearchSelect aria-label="Lọc theo nhân sự" className="min-h-9 lg:w-48" value={userId} onChange={setUserId} emptyOption="Tất cả mọi người" options={peopleOptions} />
                <label className="flex items-start gap-2 text-sm text-ink-700 sm:col-span-2 lg:basis-full">
                    <input
                        type="checkbox"
                        checked={needsReviewOnly}
                        onChange={event => setNeedsReviewOnly(event.target.checked)}
                        className="mt-0.5 h-4 w-4 rounded border-ink-300 text-academy-600 focus:ring-academy-500"
                    />
                    <span>
                        Chỉ hiện mục bất thường chưa xem{pendingCount ? ` (${pendingCount})` : ''}
                        <span className="block text-xs text-ink-500">
                            Điểm danh ngoài phạm vi hoặc không có vị trí, đăng ký lớp khi chưa điểm danh hợp lệ, giáo viên/trợ giảng tự sửa giờ.
                        </span>
                    </span>
                </label>
            </div>

            {loading ? (
                <LoadingState title="Đang tải nhật ký" />
            ) : activities.length === 0 ? (
                <EmptyState title="Không có hoạt động" description="Thử chọn ngày hoặc cơ sở khác." />
            ) : (
                <ul className="divide-y divide-ink-200 overflow-hidden rounded-lg border border-ink-200 bg-white">
                    {activities.map(activity => {
                        const pending = activity.needsReview && !activity.reviewedAt;
                        return (
                            <li
                                key={activity.id}
                                id={`activity-${activity.id}`}
                                className={cn(
                                    'px-4 py-3',
                                    pending && 'bg-warning-50/50',
                                    activity.id === highlightedId && 'ring-2 ring-inset ring-academy-300',
                                )}
                            >
                                <div className="flex flex-wrap items-start justify-between gap-3">
                                    <div className="min-w-[16rem] flex-1">
                                        <div className="flex flex-wrap items-center gap-2 text-xs text-ink-500">
                                            <span className="font-semibold text-ink-700">{formatCenterTime(activity.createdAt)}</span>
                                            <StatusBadge tone="neutral">{typeLabels[activity.type]}</StatusBadge>
                                            {activity.campus ? <span>{activity.campus.name}</span> : null}
                                            {activity.class ? <span>· {activity.class.name}</span> : null}
                                        </div>
                                        <p className="mt-1 text-sm text-ink-900">{activity.summary}</p>
                                        {activity.checkIn ? <CheckInDetails checkIn={activity.checkIn} /> : null}
                                    </div>
                                    {pending ? (
                                        <Button size="sm" variant="secondary" onClick={() => markReviewed(activity.id)}>
                                            Đã xem
                                        </Button>
                                    ) : activity.reviewedAt ? (
                                        <span className="text-xs text-ink-400">
                                            Đã xem bởi {personName(activity.reviewedBy)} lúc {formatCenterTime(activity.reviewedAt)}
                                        </span>
                                    ) : null}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </PageContainer>
    );
}
